import '../tooltip/Tooltip.css'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { TOOLTIP_DELAY_MS } from '../tooltip/delay'

interface TooltipState {
  visible: boolean
  title: string | null
  position: { x: number; y: number }
}

interface TooltipContextType {
  setTooltip: (tooltip: TooltipState) => void
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined)

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    title: null,
    position: { x: 0, y: 0 },
  })

  return (
    <TooltipContext.Provider value={{ setTooltip }}>
      {children}
      {tooltip.position.x !== 0 && tooltip.position.y !== 0 && (
        <GlobalTooltip tooltip={tooltip} />
      )}
    </TooltipContext.Provider>
  )
}

export const useTooltip = () => {
  const context = useContext(TooltipContext)
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider')
  }
  return context
}

export const GlobalTooltip: React.FC<{ tooltip: TooltipState }> = ({
  tooltip,
}) => {
  return createPortal(
    <div className="tooltip-element-wrapper">
      <div
        className="tooltip-title"
        style={{
          position: 'absolute',
          top: tooltip.position.y,
          left: tooltip.position.x,
          display: tooltip.visible && tooltip.title ? 'block' : 'none',
        }}
      >
        {tooltip.title}
      </div>
    </div>,
    document.body
  )
}

const HIDDEN: TooltipState = {
  visible: false,
  title: null,
  position: { x: 0, y: 0 },
}

// Names the element under the pointer only once it has stayed there a while.
// `move` follows the pointer, `hide` drops the tooltip and any pending wait.
export const useDelayedTooltip = (
  title: string,
  alignment: 'left' | 'right' = 'right'
) => {
  const { setTooltip } = useTooltip()
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const shown = useRef(false)
  const mouse = useRef({ x: 0, y: 0 })
  // Set by a click, lifted when the pointer leaves: it answered the element.
  const muted = useRef(false)
  const latest = useRef({ title, alignment })
  latest.current = { title, alignment }

  const show = useCallback(() => {
    const { x, y } = mouse.current
    setTooltip({
      visible: true,
      title: latest.current.title,
      position: {
        x: latest.current.alignment === 'right' ? x + 10 : x - 10,
        y: y + 20,
      },
    })
  }, [setTooltip])

  const hide = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = undefined
    shown.current = false
    setTooltip(HIDDEN)
  }, [setTooltip])

  const mute = useCallback(() => {
    muted.current = true
    hide()
  }, [hide])

  const leave = useCallback(() => {
    muted.current = false
    hide()
  }, [hide])

  const move = useCallback(
    (e: React.MouseEvent) => {
      mouse.current = { x: e.pageX, y: e.pageY }
      if (muted.current) return
      // Nothing to say: leave the shared tooltip to whoever else writes it.
      if (!latest.current.title) {
        if (shown.current) hide()
        clearTimeout(timer.current)
        timer.current = undefined
        return
      }
      if (shown.current) return show()
      timer.current ??= setTimeout(() => {
        timer.current = undefined
        if (!latest.current.title) return
        shown.current = true
        show()
      }, TOOLTIP_DELAY_MS)
    },
    [hide, show]
  )

  useEffect(
    () => () => {
      clearTimeout(timer.current)
      timer.current = undefined
    },
    []
  )

  return { move, hide, mute, leave }
}

export const Tooltip: React.FC<{
  title: string
  children: React.ReactNode
  alignment?: 'left' | 'right'
}> = ({ title, children, alignment = 'right' }) => {
  const { move, hide, mute, leave } = useDelayedTooltip(title, alignment)

  useEffect(() => hide, [hide])

  return (
    <div
      className="tooltip-element-wrapper"
      onMouseMove={move}
      onMouseLeave={leave}
      // a click answers the pointer; a tooltip popping up after it is noise
      onMouseDown={mute}
    >
      {children}
    </div>
  )
}

export default GlobalTooltip
